import {useEffect} from 'react';

import {useKeenSlider} from 'keen-slider/react';
import {useTranslation} from 'react-i18next';

import type {WalkSliderCard} from './types';

/**
 * Custom hook for the `MapWalkSlider` component.
 * Wraps keen-slider (`perView: 'auto'`, so the expanded card and the compact cards can have
 * different widths) and scrolls the track to whichever position the selected walk's card
 * already occupies whenever the selection changes — the walk order itself never changes.
 *
 * @param selectedWalkId The ID of the currently selected (expanded) walk, if any.
 * @param walks          Ordered array of walk cards, in a fixed order that never changes.
 * @returns An object containing `sliderRef`, the ref callback for the slider track element, and `t`.
 */
export const useMapWalkSlider = (selectedWalkId: string | null, walks: WalkSliderCard[]) => {
    const [sliderRef, sliderInstanceRef] = useKeenSlider<HTMLDivElement>({
        slides: {
            perView: 'auto',
            spacing: 12
        }
    });
    const {t} = useTranslation();

    useEffect(() => {
        const selectedIndex = walks.findIndex(walk => walk.id === selectedWalkId);

        if (selectedIndex >= 0) sliderInstanceRef.current?.moveToIdx(selectedIndex);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedWalkId]);

    return {
        sliderRef,
        t
    };
};